import { createSerializer, parseAsStringLiteral } from "nuqs/server";
import type {
  UserAnalyticsOnboardingBootstrapJob,
  UserAnalyticsOnboardingBusinessContext,
  UserAnalyticsOnboardingSession,
} from "./graphql";

export type UserAnalyticsBusinessContextForm = {
  analysisFocus: string;
  targetSegments: string;
};

export const emptyUserAnalyticsBusinessContextForm: UserAnalyticsBusinessContextForm =
  {
    analysisFocus: "",
    targetSegments: "",
  };

const canonicalUserAnalyticsOnboardingStepSlugs = [
  "source",
  "inputs",
  "results",
] as const;

const userAnalyticsOnboardingStepSlugs = [
  ...canonicalUserAnalyticsOnboardingStepSlugs,
] as const;

export type UserAnalyticsOnboardingStepSlug =
  (typeof userAnalyticsOnboardingStepSlugs)[number];

export const userAnalyticsOnboardingStepParser = parseAsStringLiteral(
  userAnalyticsOnboardingStepSlugs,
);

/**
 * UA onboarding の遷移先を、許可済み `step` slug の query として生成します。
 * default step は補わず、`null` は省略して、未対応 slug は型 contract で受け付けません。
 */
export const serializeUserAnalyticsOnboardingSearchParams = createSerializer({
  step: userAnalyticsOnboardingStepParser,
});

/**
 * UA 初期分析の保存済み入力と job 状態から、再開時に表示する step を決めます。
 * backend status だけでなく必須入力の有無も見て、途中保存された session でも安全な step に戻します。
 */
export function getUserAnalyticsOnboardingActiveStep(
  session: UserAnalyticsOnboardingSession | null,
  job: UserAnalyticsOnboardingBootstrapJob | null,
) {
  const bigQuerySource = session?.dataSource?.bigQuerySource;
  if (!bigQuerySource || !session?.dateRange) {
    return 0;
  }
  if ((session.keyEvents?.length ?? 0) === 0) {
    return 1;
  }
  if (session.status === "READY") {
    return 2;
  }
  if (job || session.latestSnapshotID) {
    return 2;
  }
  return 1;
}

/**
 * URL に保持する UA 初期分析 step slug を表示 step index から取得します。
 * 範囲外の index は、wizard が描画できる 0-3 に丸めます。
 */
export function userAnalyticsOnboardingStepSlug(
  stepIndex: number,
): (typeof canonicalUserAnalyticsOnboardingStepSlugs)[number] {
  const index = Math.min(
    canonicalUserAnalyticsOnboardingStepSlugs.length - 1,
    Math.max(0, Math.trunc(stepIndex)),
  );
  return canonicalUserAnalyticsOnboardingStepSlugs[index] ?? "source";
}

/**
 * URL の `step` slug を UA wizard の表示 index に変換します。
 */
export function userAnalyticsOnboardingStepIndex(
  value: UserAnalyticsOnboardingStepSlug,
) {
  return canonicalUserAnalyticsOnboardingStepSlugs.indexOf(value);
}

/**
 * UA 初期分析の分析焦点を backend input へ変換します。
 * worker/LLM 側が焦点として読めるよう、自由文を `primaryGoal` に明示保存します。
 */
export function toUserAnalyticsBusinessContextInput(
  form: UserAnalyticsBusinessContextForm,
) {
  return {
    businessName: null,
    primaryGoal: trimmedOrNull(form.analysisFocus),
    trafficNotes: null,
    behaviorNotes: null,
    conversionNotes: null,
    exclusionNotes: null,
    targetSegments: listUserAnalyticsOnboardingUniqueEvents([
      form.targetSegments,
    ]).slice(0, 5),
  };
}

/**
 * 保存済み context を、現在 UI が扱う分析焦点に戻します。
 * `primaryGoal` が空の場合は `conversionNotes` を表示用の補助値にします。
 */
export function toUserAnalyticsBusinessContextForm(
  context: UserAnalyticsOnboardingBusinessContext,
): UserAnalyticsBusinessContextForm {
  return {
    analysisFocus: context.primaryGoal ?? context.conversionNotes ?? "",
    targetSegments: context.targetSegments?.join(", ") ?? "",
  };
}

/**
 * 完了済みまたは preview-ready の job だけを結果 step に固定するための判定です。
 */
export function isFinalUserAnalyticsBootstrapStatus(
  status: UserAnalyticsOnboardingBootstrapJob["status"] | undefined,
) {
  return status === "PREVIEW_READY" || status === "FAILED";
}

/**
 * start mutation の戻り値は即時表示用、query job は polling 後の source of truth として扱います。
 * requeue 直後だけは古い terminal query を退け、query の updatedAt が追いついた時点で
 * source of truth を戻すことで、QUEUED を保持したまま preview-ready へ進まない UI を防ぎます。
 */
export function selectUserAnalyticsOnboardingBootstrapJob({
  session,
  mutationJob,
  queryJob,
}: {
  session: UserAnalyticsOnboardingSession | null;
  mutationJob: UserAnalyticsOnboardingBootstrapJob | null;
  queryJob: UserAnalyticsOnboardingBootstrapJob | null;
}) {
  const isRequeuedMutationPending = mutationJob?.status === "QUEUED";
  const isQueryStillPreviousTerminalJob =
    queryJob &&
    isFinalUserAnalyticsBootstrapStatus(queryJob.status) &&
    !userAnalyticsQueriedBootstrapJobIsAtLeastAsRecent({
      mutationJob,
      queryJob,
    });
  const latestJob =
    isRequeuedMutationPending && isQueryStillPreviousTerminalJob
      ? mutationJob
      : (queryJob ?? mutationJob);
  if (!latestJob) {
    return null;
  }
  if (session?.status === "READY" && !mutationJob) {
    return null;
  }
  return latestJob;
}

/** mutation より新しい terminal query だけを現在の job として採用するための時刻比較です。 */
function userAnalyticsQueriedBootstrapJobIsAtLeastAsRecent({
  mutationJob,
  queryJob,
}: {
  mutationJob: UserAnalyticsOnboardingBootstrapJob | null;
  queryJob: UserAnalyticsOnboardingBootstrapJob;
}) {
  if (!mutationJob) {
    return true;
  }
  const mutationUpdatedAt = Date.parse(mutationJob.updatedAt);
  const queryUpdatedAt = Date.parse(queryJob.updatedAt);
  return (
    Number.isFinite(mutationUpdatedAt) &&
    Number.isFinite(queryUpdatedAt) &&
    queryUpdatedAt >= mutationUpdatedAt
  );
}

export function shouldShowUserAnalyticsStartingAction({
  isStarting,
  job,
}: {
  isStarting: boolean;
  job: UserAnalyticsOnboardingBootstrapJob | null;
}) {
  return isStarting && !isFinalUserAnalyticsBootstrapStatus(job?.status);
}

/**
 * partial preview は適用 retry の説明を出し、queued/running は polling を続けます。
 */
export function userAnalyticsPreviewCanBeApplied({
  job,
  session,
}: {
  job: UserAnalyticsOnboardingBootstrapJob | null;
  session: UserAnalyticsOnboardingSession | null;
}) {
  if (session?.status === "APPLIED") {
    return true;
  }
  return job?.status === "PREVIEW_READY";
}

/**
 * UA 初期分析の対象期間を、SA 初期分析と同じ 28 日間に固定します。
 * `latestDataDate` がある場合はその日を終端にし、未取得時は UTC の今日を終端にします。
 */
export function userAnalyticsOnboardingFixedDateRange(
  latestDataDate: string | null | undefined,
) {
  const endDate = userAnalyticsOnboardingDateFromISODate(latestDataDate);
  endDate.setUTCDate(endDate.getUTCDate() - 27);
  const startDate = userAnalyticsOnboardingISODate(endDate);
  const end = userAnalyticsOnboardingDateFromISODate(latestDataDate);
  return {
    startDate,
    endDate: userAnalyticsOnboardingISODate(end),
  };
}

/**
 * API 由来の eventNames から、初期選択する key event 候補を決めます。
 * GA4 の一般的な conversion event を優先し、該当がなければ頻度順の先頭候補を使います。
 */
export function initialUserAnalyticsOnboardingKeyEvents({
  savedKeyEvents,
  suggestedKeyEvents,
}: {
  savedKeyEvents?: readonly string[] | null;
  suggestedKeyEvents?: readonly string[] | null;
}) {
  const saved = listUserAnalyticsOnboardingUniqueEvents(savedKeyEvents ?? []);
  if (saved.length > 0) {
    return saved;
  }
  const suggestions = listUserAnalyticsOnboardingUniqueEvents(
    suggestedKeyEvents ?? [],
  );
  const priority = [
    "purchase",
    "generate_lead",
    "sign_up",
    "submit_lead_form",
    "begin_checkout",
  ];
  const prioritized = priority.filter((eventName) =>
    suggestions.includes(eventName),
  );
  if (prioritized.length > 0) {
    return prioritized.slice(0, 2);
  }
  return suggestions.slice(0, 2);
}

/**
 * 任意の複数行・カンマ区切り入力を、順序を保った重複なし配列へ変換します。
 */
function listUserAnalyticsOnboardingUniqueTextValues(
  values: readonly string[],
) {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    for (const item of value.split(/[\n,]/)) {
      const trimmed = item.trim();
      if (!trimmed || seen.has(trimmed)) {
        continue;
      }
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result;
}

/**
 * key event 名の候補入力を、空白と重複を除いた配列へ変換します。
 */
export function listUserAnalyticsOnboardingUniqueEvents(
  values: readonly string[],
) {
  return listUserAnalyticsOnboardingUniqueTextValues(values);
}

function trimmedOrNull(value: string) {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function userAnalyticsOnboardingDateFromISODate(
  value: string | null | undefined,
) {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00.000Z`);
  }
  if (value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(
        Date.UTC(
          parsed.getUTCFullYear(),
          parsed.getUTCMonth(),
          parsed.getUTCDate(),
        ),
      );
    }
  }
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
}

function userAnalyticsOnboardingISODate(date: Date) {
  return date.toISOString().slice(0, 10);
}
